import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useStore } from "@/store/useStore";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { GameBoard } from "@/components/chess/GameBoard";
import { GameClock } from "@/components/chess/GameClock";
import { MoveList, replayMoves } from "@/components/chess/MoveList";
import { Chess } from "chess.js";
import { Flag, RotateCcw, Coins, User, Wifi, WifiOff, Download, Volume2, VolumeX, Loader2, ShieldAlert } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import WebcamMonitor from "@/components/WebcamMonitor";
import { useChessSounds } from "@/hooks/useChessSounds";
import { API_URL, getWsUrl } from "@/config/api";

const WS_URL = getWsUrl();

export default function LiveGamePage() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token, boardTheme } = useStore();
  
  const [game, setGame] = useState(null);
  const [chess] = useState(new Chess());
  const [fen, setFen] = useState("start");
  const [playerColor, setPlayerColor] = useState("white");
  const [fairplayWarning, setFairplayWarning] = useState(false);
  const [myTime, setMyTime] = useState(600);
  const [opponentTime, setOpponentTime] = useState(600);
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [gameStatus, setGameStatus] = useState("connecting");
  const [winner, setWinner] = useState(null);
  const [winReason, setWinReason] = useState(null);
  const [showResignDialog, setShowResignDialog] = useState(false);
  const [showDrawDialog, setShowDrawDialog] = useState(false);
  const [drawOfferedBy, setDrawOfferedBy] = useState(null);
  const [connected, setConnected] = useState(false);
  const [opponentConnected, setOpponentConnected] = useState(true);
  const [moves, setMoves] = useState([]);
  const [ratingChange, setRatingChange] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  const wsRef = useRef(null);
  const lastMoveTimeRef = useRef(Date.now());
  const lowTimeWarningRef = useRef(false);
  const handlerRef = useRef(null);
  const lastMoveRef = useRef("");
  const gameStatusRef = useRef("connecting");
  gameStatusRef.current = gameStatus;
  
  // Sound effects
  const { sounds } = useChessSounds(soundEnabled);

  // Connect to WebSocket
  useEffect(() => {
    if (!token || !gameId) return;
    let disposed = false;

    const connectWs = () => {
      const ws = new WebSocket(`${WS_URL}/api/ws/game/${gameId}?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected");
        setConnected(true);
        // Send ping every 30 seconds
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000);
        ws.pingInterval = pingInterval;
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (handlerRef.current) handlerRef.current(data);
      };

      ws.onclose = (event) => {
        console.log("WebSocket closed:", event.code, event.reason);
        setConnected(false);
        clearInterval(ws.pingInterval);
        
        // Reconnect after 2 seconds if not intentionally closed
        if (!disposed && event.code !== 1000 && ["active", "waiting", "connecting"].includes(gameStatusRef.current)) {
          setTimeout(() => { if (!disposed) connectWs(); }, 2000);
        }
      };

      ws.onerror = () => {
        if (!disposed) setConnected(false);
      };
    };

    connectWs();

    return () => {
      disposed = true;
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
      }
    };
  }, [token, gameId]);

  // Low time warning sound (clock rendering itself is handled by GameClock)
  useEffect(() => {
    if (gameStatus !== "active" || !isMyTurn) return undefined;
    const id = setInterval(() => {
      const elapsed = (Date.now() - lastMoveTimeRef.current) / 1000;
      const remaining = myTime - elapsed;
      if (remaining <= 10 && remaining > 0) sounds.lowTime();
    }, 1000);
    return () => clearInterval(id);
  }, [isMyTurn, gameStatus, myTime, sounds]);

  const [viewIndex, setViewIndex] = useState(null);
  const steps = useMemo(() => replayMoves(moves), [moves]);
  const liveStep = steps[steps.length - 1];
  useEffect(() => {
    if (viewIndex !== null && viewIndex >= steps.length - 1) setViewIndex(null);
  }, [steps.length]);

  const handleGameEnd = useCallback((winner, reason) => {
    const isWinner = winner === playerColor;
    const isDraw = winner === "draw";
    const stake = game?.stake || 0;
    
    const reasonText = t(`live.reasons.${reason}`, reason);
    
    if (isDraw) {
      toast.info(`${t("live.draw")} ${reasonText}`, { duration: 5000 });
    } else if (isWinner) {
      const prize = stake * 2 * 0.95;
      toast.success(`${t("live.victory")} ${reasonText}${stake > 0 ? ` +${prize.toFixed(2)} Points` : ""}`, { duration: 5000 });
    } else {
      toast.error(`${t("live.defeat")}: ${reasonText}${stake > 0 ? ` -${stake} Points` : ""}`, { duration: 5000 });
    }
  }, [playerColor, game, t]);

  const handleWsMessage = useCallback((data) => {
    console.log("WS message:", data);
    
    switch (data.type) {
      case "game_state":
        // Initial game state
        const gameData = data.game;
        setGame(gameData);
        setPlayerColor(data.your_color);
        
        if (gameData.fen) {
          chess.load(gameData.fen);
          setFen(gameData.fen);
        }
        
        setMyTime(data.your_color === "white" ? gameData.white_time : gameData.black_time);
        setOpponentTime(data.your_color === "white" ? gameData.black_time : gameData.white_time);
        setIsMyTurn((gameData.current_turn || gameData.turn) === data.your_color);
        setGameStatus(gameData.status);
        setWinner(gameData.winner);
        setMoves(gameData.san_moves || gameData.moves || []);
        lastMoveTimeRef.current = Date.now();
        if (gameData.draw_offered_by) {
          setDrawOfferedBy(gameData.draw_offered_by);
          if (gameData.draw_offered_by !== data.your_color) setShowDrawDialog(true);
        }
        
        // Play game start sound if game is active
        if (gameData.status === "active") {
          sounds.gameStart();
        }
        break;
        
      case "game_started": {
        const started = data.game;
        setGame(started);
        setGameStatus(started.status);
        setMyTime(playerColor === "white" ? started.white_time : started.black_time);
        setOpponentTime(playerColor === "white" ? started.black_time : started.white_time);
        setIsMyTurn((started.current_turn || started.turn) === playerColor);
        setOpponentConnected(true);
        lastMoveTimeRef.current = Date.now();
        sounds.gameStart();
        toast.success(t("live.opponentJoined"));
        break;
      }

      case "move":
        if (data.move && lastMoveRef.current === `${data.fen}|${data.move}`) break;
        lastMoveRef.current = `${data.fen}|${data.move}`;
        // Move made - determine sound type
        const moveStr = data.move || "";
        const prevFen = fen;
        chess.load(data.fen);
        
        // Detect move type for appropriate sound
        if (chess.isCheck()) {
          sounds.check();
        } else if (moveStr.includes("O-O") || moveStr.includes("0-0")) {
          sounds.castle();
        } else if (data.is_capture || moveStr.includes("x")) {
          sounds.capture();
        } else {
          sounds.move();
        }
        
        setFen(data.fen);
        setIsMyTurn(data.turn === playerColor);
        setMoves(prev => [...prev, data.san || data.move]);
        lastMoveTimeRef.current = Date.now();
        lowTimeWarningRef.current = false; // Reset low time warning
        
        // Update times
        if (data.white_time !== undefined) {
          if (playerColor === "white") {
            setMyTime(data.white_time);
          } else {
            setOpponentTime(data.white_time);
          }
        }
        if (data.black_time !== undefined) {
          if (playerColor === "black") {
            setMyTime(data.black_time);
          } else {
            setOpponentTime(data.black_time);
          }
        }
        break;
        
      case "fairplay_warning":
        if (data.target === playerColor) setFairplayWarning(true);
        break;

      case "game_over":
        setGameStatus("finished");
        setWinner(data.winner);
        setWinReason(data.reason);
        
        // Play victory or defeat sound
        if (data.winner === "draw") {
          sounds.gameEnd();
        } else if (data.winner === playerColor) {
          sounds.victory();
        } else {
          sounds.defeat();
        }
        
        handleGameEnd(data.winner, data.reason);
        break;
        
      case "player_connected":
        if (data.player !== playerColor) {
          setOpponentConnected(true);
          toast.info(`${data.username} ${t("live.connected")}`);
        }
        break;
        
      case "player_disconnected":
        if (data.player !== playerColor) {
          setOpponentConnected(false);
          toast.warning(t("live.opponentDisconnected"));
        }
        break;
        
      case "draw_offered":
        if (data.by !== playerColor) {
          setDrawOfferedBy(data.by);
          setShowDrawDialog(true);
          sounds.drawOffer();
        }
        break;
        
      case "draw_declined":
        if (data.by !== playerColor) {
          toast.info(t("live.drawDeclined"));
        }
        setDrawOfferedBy(null);
        break;
        
      case "error":
        toast.error(data.message);
        sounds.illegal();
        break;
        
      case "pong":
        // Heartbeat response, ignore
        break;
    }
  }, [chess, playerColor, fen, sounds, handleGameEnd]);

  useEffect(() => {
    handlerRef.current = handleWsMessage;
  }, [handleWsMessage]);

  const sendWsMessage = (msg) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else {
      toast.error(t("live.connectionLost"));
    }
  };

  const makeMove = (uci) => {
    if (!isMyTurn || gameStatus !== "active") return false;
    const move = uci;
    try {
      const testChess = new Chess(fen);
      const result = testChess.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] || undefined });
      if (!result) {
        toast.error(t("live.illegalMove"));
        return false;
      }
    } catch (e) {
      toast.error(t("live.illegalMove"));
      return false;
    }
    
    // Send move via WebSocket
    sendWsMessage({ type: "move", move });
    return true;
  };

  const resign = () => {
    sendWsMessage({ type: "resign" });
    setShowResignDialog(false);
  };

  const offerDraw = () => {
    sendWsMessage({ type: "draw_offer" });
    setDrawOfferedBy(playerColor);
    toast.info(t("live.drawOfferSent"));
  };

  const acceptDraw = () => {
    sendWsMessage({ type: "draw_accept" });
    setShowDrawDialog(false);
  };

  const declineDraw = () => {
    sendWsMessage({ type: "draw_decline" });
    setShowDrawDialog(false);
    setDrawOfferedBy(null);
  };

  // Export PGN
  const handleExportPGN = async () => {
    try {
      const res = await fetch(`${API_URL}/api/games/${gameId}/export-pgn`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `teachess_pvp_${gameId.slice(0, 8)}.pgn`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(t("live.pgnExported"));
      } else {
        toast.error(t("live.exportError"));
      }
    } catch (err) {
      toast.error(t("live.exportError"));
    }
  };


  const getOpponentName = () => {
    if (!game) return t("live.opponent");
    return playerColor === "white" ? game.black_username : game.white_username;
  };


  // Loading state
  if (gameStatus === "connecting") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p>{t("live.connecting")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4 max-w-4xl" data-testid="live-game-page">
      {/* Connection Status & Sound Toggle */}
      <div className="flex justify-end mb-2 gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSoundEnabled(!soundEnabled)}
          className="h-7 px-2"
          data-testid="sound-toggle"
        >
          {soundEnabled ? (
            <Volume2 className="h-4 w-4 text-primary" />
          ) : (
            <VolumeX className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
        <Badge variant={connected ? "outline" : "destructive"} className="text-xs">
          {connected ? <Wifi className="h-3 w-3 mr-1" /> : <WifiOff className="h-3 w-3 mr-1" />}
          {connected ? "Online" : "Reconnecting..."}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Board */}
        <div className="lg:col-span-2">
          {/* Opponent Info */}
          <div className="flex items-center justify-between mb-2 p-3 bg-card rounded-lg border">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${opponentConnected ? "bg-green-500" : "bg-red-500"}`} />
              <User className="h-5 w-5" />
              <span className="font-medium">{getOpponentName()}</span>
              {game?.stake > 0 && (
                <Badge variant="outline" className="text-yellow-500">
                  <Coins className="h-3 w-3 mr-1" />
                  {game.stake} Points
                </Badge>
              )}
            </div>
            <GameClock time={opponentTime} running={!isMyTurn && gameStatus === "active"} testId="opponent-clock" />
          </div>

          {/* Chess Board */}
          <div className="relative rounded-lg shadow-xl mt-3">
            {viewIndex !== null && (
              <button
                type="button"
                onClick={() => setViewIndex(null)}
                className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-background/95 border border-primary/50 text-xs font-medium text-primary shadow"
                data-testid="back-to-live"
              >
                {t("game.viewingHistory", "Просмотр хода")} {viewIndex}/{steps.length - 1} · {t("game.backToLive", "к партии")}
              </button>
            )}
            <GameBoard
              id="live-board"
              fen={viewIndex !== null ? steps[viewIndex].fen : fen}
              orientation={playerColor}
              myColor={playerColor}
              canMove={isMyTurn && gameStatus === "active" && viewIndex === null}
              onMove={makeMove}
              lastMove={viewIndex !== null ? steps[viewIndex] : liveStep}
              theme={boardTheme}
              testIdPrefix="live"
            />
          </div>

          {/* Player Info */}
          <div className="flex items-center justify-between mt-2 p-3 bg-card rounded-lg border">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <User className="h-5 w-5 text-primary" />
              <span className="font-medium">{user?.username}</span>
              <Badge variant="outline">{playerColor === "white" ? t("live.white") : t("live.black")}</Badge>
            </div>
            <GameClock time={myTime} running={isMyTurn && gameStatus === "active"} testId="my-clock" />
          </div>
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* Game Status */}
          <Card>
            <CardContent className="pt-4">
              {gameStatus === "active" ? (
                <div className="text-center">
                  <p className={`text-lg font-bold ${isMyTurn ? "text-primary" : "text-muted-foreground"}`}>
                    {isMyTurn ? t("live.yourTurn") : t("live.opponentTurn")}
                  </p>
                  {game?.stake > 0 && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {t("live.pot")}: {game.stake * 2} Points
                    </p>
                  )}
                </div>
              ) : gameStatus === "waiting" ? (
                <div className="text-center" data-testid="waiting-for-opponent">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-primary" />
                  <p className="text-lg font-bold">{t("live.waiting")}</p>
                  <p className="text-sm text-muted-foreground mt-1">{t("live.waitingHint")}</p>
                  <Button variant="outline" className="mt-4" onClick={() => navigate("/lobby")}>{t("nav.lobby")}</Button>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-lg font-bold">
                    {winner === "draw" ? t("live.draw") : winner === playerColor ? t("live.victory") : t("live.defeat")}
                  </p>
                  <p className="text-sm text-muted-foreground capitalize">{winReason}</p>
                  <Button className="mt-4" onClick={() => navigate("/matchmaking")}>
                    {t("live.playAgain")}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Moves List */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">{t("live.moves")} ({moves.length})</p>
                {moves.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleExportPGN}
                    className="h-7 px-2"
                    data-testid="export-pgn-btn"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    PGN
                  </Button>
                )}
              </div>
              <MoveList steps={steps} viewIndex={viewIndex} onSelect={setViewIndex} />
            </CardContent>
          </Card>

          {/* Actions */}
          {gameStatus === "active" && (
            <Card>
              <CardContent className="pt-4 space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={offerDraw}
                  data-testid="offer-draw-btn"
                  disabled={drawOfferedBy === playerColor}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {drawOfferedBy === playerColor ? t("live.drawOffered") : t("live.offerDraw")}
                </Button>
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setShowResignDialog(true)}
                  data-testid="resign-btn"
                >
                  <Flag className="h-4 w-4 mr-2" />
                  {t("live.resign")}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Webcam Monitoring for high-stake games */}
          {gameStatus === "active" && game?.stake >= 100 && (
            <Card>
              <CardContent className="pt-4">
                <WebcamMonitor 
                  gameId={gameId}
                  token={token}
                  stake={game?.stake || 0}
                  onAlert={(alert) => {
                    console.log("Webcam alert:", alert);
                  }}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Resign Dialog */}
      <AlertDialog open={showResignDialog} onOpenChange={setShowResignDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("live.resign")}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t("live.resignConfirm")}
              {game?.stake > 0 && ` ${t("live.youLose")} ${game.stake} Points.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={resign} className="bg-destructive">
              {t("live.resign")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Fair-play warning */}
      <AlertDialog open={fairplayWarning} onOpenChange={setFairplayWarning}>
        <AlertDialogContent data-testid="fairplay-warning-dialog" className="border-yellow-500/50">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-yellow-500">
              <ShieldAlert className="h-5 w-5" />{t("fairplay.warnTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>{t("fairplay.warnText")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setFairplayWarning(false)} data-testid="fairplay-warning-ok">{t("fairplay.understood")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Draw Offer Dialog */}
      <AlertDialog open={showDrawDialog} onOpenChange={setShowDrawDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("live.drawOfferTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("live.drawOfferText")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={declineDraw}>{t("live.decline")}</AlertDialogCancel>
            <AlertDialogAction onClick={acceptDraw}>{t("live.accept")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
